import React, { useEffect, useCallback, useState } from 'react'
import styled from 'styled-components'
import { useCarousel, useProductState } from '@sentecacommerce-theme/lib'
import {
  LazyResponsiveImage,
  IconVideo,
  CarouselArrowIcon,
} from '../../../Common'

export const DesktopPreviewCarousel = ({
  currentSlide,
  slideToIndex,
  videos = [],
}: {
  currentSlide: number
  slideToIndex: (index: number) => void
  videos: any[]
}) => {
  const { metaData, selectedVariant } = useProductState()
  const [Carousel, carousel] = useCarousel({
    containScroll: 'trimSnaps',
    axis: 'y',
    startIndex: currentSlide,
  })
  const scrollPrev = useCallback(() => carousel?.scrollPrev(), [carousel])
  const scrollNext = useCallback(() => carousel?.scrollNext(), [carousel])
  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false)
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false)

  useEffect(() => {
    carousel?.scrollTo(currentSlide)
  }, [currentSlide])

  const handleSlideClick = (index: number) => () => {
    if (!carousel?.clickAllowed()) return
    slideToIndex(index)
  }

  useEffect(() => {
    if (carousel) {
      const onSelect = () => {
        setPrevBtnEnabled(carousel.canScrollPrev())
        setNextBtnEnabled(carousel.canScrollNext())
      }
      carousel.on('select', onSelect)
      onSelect()
    }
  }, [carousel])

  const firstImage = selectedVariant?.images?.[0]

  const images = [
    firstImage,
    ...videos,
    ...(selectedVariant?.images?.slice(1) ?? []),
  ]

  return (
    <Container>
      <SlidesContainer>
        <Carousel>
          <SlidesWrapper>
            {images?.map((image, index) => {
              return (
                <Slide onClick={handleSlideClick(index)} key={`image_${index}`}>
                  <ImageContainer isSelected={index === currentSlide}>
                    {(image as any).type === 'video' ? (
                      <VideoWrapper>
                        <LazyResponsiveImage
                          breakpointSizes={{}}
                          defaultImageWidth="70px"
                          defaultSize="thumb"
                          imagesMap={image}
                          alt={metaData.name}
                        />
                        <IconVideo />
                      </VideoWrapper>
                    ) : (
                      <LazyResponsiveImage
                        breakpointSizes={{}}
                        defaultImageWidth="70px"
                        imagesMap={image}
                        alt={metaData.name}
                      />
                    )}
                  </ImageContainer>
                </Slide>
              )
            })}
          </SlidesWrapper>
        </Carousel>
      </SlidesContainer>
      <UpArrow isEnabled={prevBtnEnabled} onClick={scrollPrev} />
      <DownArrow isEnabled={nextBtnEnabled} onClick={scrollNext} />
    </Container>
  )
}
interface IsActivatable {
  isEnabled: boolean
}
const Container = styled.div`
  position: relative;
  width: 8rem;
  height: 40rem;
`

const SlidesContainer = styled.div`
  margin-bottom: 0;
  position: relative;
  z-index: 1;

  width: 8rem;
  height: 40rem;
  overflow: hidden;
`

const SlidesWrapper = styled.div`
  padding-bottom: 0;
  display: flex;
  flex-direction: column;
  height: 40rem;
`

const Slide = styled.figure.attrs({
  className: 'py-2 px-0',
})`
  width: 8rem;
  height: 9.4rem;
  box-sizing: border-box;
  cursor: pointer;
`

const ImageContainer = styled.div.attrs({
  className: 'p-2',
})<{ isSelected?: boolean }>`
  transition: opacity 0.15s ease, border-color 0.15s ease;
  opacity: ${(props) => (props.isSelected ? 1 : '0.6')};
  border: 1px solid;
  border-color: ${(props) =>
    props.theme.palette[props.isSelected ? 'silverTree' : 'disabled']};
  &:hover {
    opacity: 0.8;
    border-color: ${({ theme }) => theme.palette.greyTitle};
  }
`

const VideoWrapper = styled.div`
  position: relative;
  & svg {
    position: absolute;
    left: 0;
    right: 0;
    margin-left: auto;
    margin-right: auto;
    top: 50%;
    width: 1.6rem;
    transform: translateY(-50%);
    color: ${({ theme }) => theme.palette.white};
  }
  &:hover {
    & svg {
      color: ${({ theme }) => theme.palette.silverTree};
    }
  }
`
const Arrow = styled(CarouselArrowIcon)<IsActivatable>`
  color: ${({ theme }) => theme.palette.silverTree};

  left: 1.9rem;

  width: 4rem;
  height: 4rem;
  position: absolute;
  display: ${(props) => (props.isEnabled ? 'block' : 'none')};
  z-index: 999999;

  cursor: pointer;

  @media only screen and (max-width: ${({ theme }) =>
      theme.breakpoints.sm.max}) {
    display: none;
  }
`

const UpArrow = styled(Arrow)`
  top: -6rem;
  transform: rotate(-90deg);
`

const DownArrow = styled(Arrow)`
  bottom: -6rem;
  transform: rotate(90deg);
`
